import {cva} from 'platform/shared';

import {ForwardedRef} from 'react';

import {isNil} from 'ramda';

import {Nullish, suffixTestId, TestIdProps} from 'shared/web';

import {useFoundationTheme} from '../../hooks/useFoundationTheme';
import {useResponsivePropValue} from '../../hooks/useResponsivePropValue';
import {ValueByDevice} from '../../types/ValueByDevice';
import {cn} from '../../utils/cn';
import {ThemeColorTextPath} from '../../utils/foundationTheme';
import {getTextColorByPath} from '../../utils/getTextColorByPath';

export type HeadingLevel = 1 | 2 | 3 | 4 | 5 | 6;
export type HeadingAlign = 'left' | 'center' | 'right';

const HEADING_SIZE_CLASSES: Record<HeadingLevel, string> = {
  1: 'text-[length:var(--heading-size-1)] leading-[var(--heading-leading-1)]',
  2: 'text-[length:var(--heading-size-2)] leading-[var(--heading-leading-2)]',
  3: 'text-[length:var(--heading-size-3)] leading-[var(--heading-leading-3)]',
  4: 'text-[length:var(--heading-size-4)] leading-[var(--heading-leading-4)]',
  5: 'text-[length:var(--heading-size-5)] leading-[var(--heading-leading-5)]',
  6: 'text-[length:var(--heading-size-6)] leading-[var(--heading-leading-6)]',
};

const HEADING_ALIGN_CLASSES: Record<HeadingAlign, string> = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
};

const headingVariants = cva(
  '[font-family:var(--font-family-heading,Roboto,_sans-serif)] tracking-[var(--letter-spacing-heading,0)]',
  {
    variants: {
      weight: {
        default: 'font-[var(--heading-weight-default)]',
        alternative: 'font-[var(--heading-weight-alternative)]',
      },
      display: {
        block: 'block',
        inline: 'inline',
      },
      truncate: {
        true: 'truncate',
        false: 'text-clip whitespace-normal overflow-visible',
      },
      overflowWrap: {
        anywhere: '[overflow-wrap:anywhere]',
        initial: '[overflow-wrap:initial]',
      },
    },
    defaultVariants: {
      weight: 'default',
      display: 'block',
      truncate: false,
      overflowWrap: 'initial',
    },
  }
);

export interface HeadingProps extends TestIdProps {
  ref?: ForwardedRef<HTMLDivElement>;
  size: HeadingLevel;
  isSingleLine?: boolean;
  alternative?: true;
  headingLevel?: HeadingLevel;
  children?: Nullish | string;
  color?: ThemeColorTextPath;
  overflowWrap?: 'anywhere';
  align?: HeadingAlign | ValueByDevice<HeadingAlign>;
  isInline?: boolean;
}

export function Heading(props: HeadingProps) {
  const theme = useFoundationTheme();
  const align = useResponsivePropValue(props.align);

  return (
    <div
      ref={props.ref}
      role="heading"
      aria-level={props.headingLevel ?? props.size}
      className={cn(
        headingVariants({
          weight: props.alternative ? 'alternative' : 'default',
          display: props.isInline ? 'inline' : 'block',
          truncate: Boolean(props.isSingleLine),
          overflowWrap: props.overflowWrap ?? 'initial',
        }),
        HEADING_SIZE_CLASSES[props.size],
        isNil(align) ? undefined : HEADING_ALIGN_CLASSES[align]
      )}
      style={{color: getTextColorByPath(props.color ?? 'primary', theme)}}
      data-testid={suffixTestId('heading', props)}
    >
      {props.children}
    </div>
  );
}
